import type { ReactNode } from 'react';
import { XIcon } from 'lucide-react';

interface Props {
  /** What the pending comment is anchored to, shown as authored. */
  label: string;
  /** Spoken description of the anchor; the visible label alone does not say what it means. */
  ariaLabel: string;
  /** Leading glyph identifying the kind of anchor. */
  icon?: ReactNode;
  /** Present only when the anchor can be dropped without leaving the composer. */
  onClear?: () => void;
  clearLabel?: string;
  /** `warning` is the established look for a text highlight; `accent` is the section pill. */
  tone?: 'warning' | 'accent';
  testId?: string;
}

const TONE_CLASSES: Record<'warning' | 'accent', string> = {
  warning: 'bg-warning text-warning-foreground',
  accent: 'bg-accent text-foreground',
};

/**
 * The composer's "this comment will be attached to X" pill, shared by every anchor kind: a
 * text range, a whole section, anything added later. Built once rather than per kind, because
 * the question it answers is the same one whatever the anchor turns out to be.
 *
 * It appears twice: in the composer while a comment is being drafted, and on the published
 * comment showing what it was anchored to. Measured against
 * `.local/prototypes/mf-brief-improvements-150926.html`, the two states differ only where the
 * clear control does — clearable is `radius-md` with asymmetric padding making room for the
 * x, published is the tighter symmetric `radius-sm` pill capped at 200px. So the shape follows
 * `onClear` rather than needing a second component. Shared by both: the accent surface, caption
 * type, 2px gap, a 12px leading glyph and an ellipsised name.
 */
export function CommentAnchorChip({
  label,
  ariaLabel,
  icon,
  onClear,
  clearLabel,
  tone = 'accent',
  testId,
}: Props) {
  return (
    <span
      data-testid={testId}
      aria-label={ariaLabel}
      className={`inline-flex shrink-0 select-none items-center gap-0.5 self-start py-1 text-caption leading-none ${
        onClear ? 'max-w-full rounded-md pl-2 pr-1' : 'max-w-[200px] rounded-sm px-2'
      } ${TONE_CLASSES[tone]}`}
    >
      {icon}
      <span className="truncate">{label}</span>
      {onClear && (
        <button
          type="button"
          aria-label={clearLabel}
          onClick={onClear}
          className="flex shrink-0 items-center justify-center rounded-sm p-0.5 text-foreground-subtle hover:text-foreground"
        >
          <XIcon className="size-3" />
        </button>
      )}
    </span>
  );
}
